import { useCallback, useEffect, useRef, useState } from "react";
import ThumbsUpBurst from "@/components/ThumbsUpBurst";
import axios from "axios";
import { celebratePayment } from "@/lib/paymentCelebration";
import { playPaymentChime } from "@/lib/chime";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Banknote, Clock, Loader2, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import HubtelPaySheet from "@/components/HubtelPaySheet";
import ServiceChargePaySheet from "@/components/ServiceChargePaySheet";
import { payLaundryBill } from "@/utils/laundryPay";
import { API, useAuth } from "@/App";
import { blockPopups, unblockPopups } from "@/lib/launchPopups";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace("https://", "wss://").replace("http://", "ws://");
const fmt = (n) => `GHS ${Number(n || 0).toFixed(2)}`;
const hideKey = (it) => `yorm:payprompt-hide:${it.kind}:${it.id}:${it.cash ? 1 : 0}`;
// "Hide for now" lasts 2 minutes, then the bill comes back until it's paid.
const HIDE_MS = 2 * 60 * 1000;
const isHidden = (it) => { try { return Date.now() - Number(sessionStorage.getItem(hideKey(it)) || 0) < HIDE_MS; } catch { return false; } };
const first = (n, fb) => (n ? n.split(" ")[0] : fb);
const bump = () => window.dispatchEvent(new Event("yorm:booking-updated"));

const Shell = ({ children, title, sub, onHide }) => (
  <Dialog open onOpenChange={(v) => { if (!v && onHide) onHide(); }}>
    <DialogContent hideCloseButton={!onHide} className="pay-glass max-w-md p-0 gap-0 max-h-[90vh] overflow-y-auto bg-[#F2F2F7]" data-testid="payment-prompt-sheet">
      <div className="px-5 pt-7 pb-5 space-y-4">
        <div className="text-center">
          <DialogTitle className="text-[13px] font-semibold uppercase tracking-wider text-gray-500">{title}</DialogTitle>
          <DialogDescription className="sr-only">{sub}</DialogDescription>
        </div>
        {children}
        {onHide && <button type="button" onClick={onHide} className="w-full text-[15px] font-medium text-gray-500 pt-1" data-testid="payment-prompt-hide">Hide for now</button>}
      </div>
    </DialogContent>
  </Dialog>
);

const Bill = ({ it, pro }) => (
  <div className="rounded-2xl bg-white p-5 text-center" data-testid={`payment-prompt-bill-${it.id}`}>
    <p className="text-[13px] text-gray-500">{it.label} with {pro ? it.customer_name : it.pro_name}</p>
    <p className="text-[34px] font-bold text-gray-900 leading-tight mt-1" data-testid="payment-prompt-amount">{fmt(it.amount)}</p>
  </div>
);

// Live laundry trip: the sheet can be brought down; the pay tile under the trip map re-opens it.
const CancelTrip = ({ it, token, onChanged }) => {
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!sure) { setSure(true); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/rideshare/bookings/${it.id}/status?token=${token}`, { status: "cancelled", cancel_reason: "other", cancel_note: "Cancelled at payment" });
      toast.success("Trip cancelled");
      onChanged(); bump();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't cancel the trip"); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={cancel} disabled={busy} className="w-full text-[15px] font-medium text-red-600 pt-1" data-testid="payment-prompt-cancel-trip">
      {busy ? "Cancelling…" : sure ? "Tap again to cancel the trip" : "Cancel trip"}
    </button>
  );
};

export const CustomerBill = ({ it, token, onHide, onMomo, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const cash = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/hubtel/pay/cash?token=${token}`, { target_type: it.kind, target_id: it.id });
      toast.success(`${first(it.pro_name, "Your pro")} knows you'll pay cash`);
      onChanged(); bump();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save your choice"); } finally { setBusy(false); }
  };
  if (it.momo_pending) {
    return (
      <Shell title="Waiting for MoMo" sub="MoMo payment pending" onHide={onHide}>
        <Bill it={it} />
        <div className="rounded-2xl bg-white p-4 flex gap-3 items-center" data-testid="payment-prompt-momo-waiting">
          <Loader2 size={20} className="animate-spin text-[#4F988E] shrink-0" />
          <p className="text-[14px] text-gray-700">Approve the MoMo prompt on your phone and enter your PIN. This closes once the payment goes through.</p>
        </div>
      </Shell>
    );
  }
  if (it.cash) {
    return (
      <Shell title="Waiting for confirmation" sub="Cash payment pending" onHide={onHide}>
        <Bill it={it} />
        <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4 flex gap-3 items-start" data-testid="payment-prompt-cash-waiting">
          <Clock size={20} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-[14px] text-amber-900">You chose cash. Hand {fmt(it.amount)} to {first(it.pro_name, "your pro")} — this closes once they confirm they got it.</p>
        </div>
        <Button className="w-full h-12 rounded-xl bg-white text-[#2F6F65] border border-[#4F988E] hover:bg-[#F0F7F6] text-[16px] font-semibold" onClick={onMomo} data-testid="payment-prompt-momo-instead">
          <Smartphone size={18} className="mr-2" /> {it.kind === "laundry_bill" ? "Pay in the app instead" : "Pay with MoMo instead"}
        </Button>
        {it.live && <CancelTrip it={it} token={token} onChanged={onChanged} />}
      </Shell>
    );
  }
  return (
    <Shell title={it.live ? `Pay your ${it.pro_role || "rider"}` : "Time to pay"} sub={it.live ? `Your ${it.pro_role || "rider"} is here` : "Your job is complete"} onHide={onHide}>
      <Bill it={it} />
      {it.tip > 0 && (
        <p className="text-center text-[13px] text-[#3d7a72] -mt-1" data-testid="payment-prompt-tip-line">
          Includes your GHS {Number(it.tip).toFixed(2)} tip — paid with MoMo it goes straight to {first(it.pro_name, "your pro")}; with cash, just add it to what you hand over.
        </p>
      )}
      <p className="text-center text-[14px] text-gray-600" data-testid="payment-prompt-message">{it.kind === "laundry_bill" ? `${it.pro_name} has sent your bill.` : it.live ? `${first(it.pro_name, `Your ${it.pro_role || "rider"}`)} has arrived.` : `${first(it.pro_name, "Your pro")} has finished.`} How would you like to pay?</p>
      <Button className="w-full h-12 rounded-xl bg-[#4F988E] hover:bg-[#3d7a72] text-[16px] font-semibold" onClick={onMomo} data-testid="payment-prompt-pay-momo">
        <Smartphone size={18} className="mr-2" /> {it.kind === "laundry_bill" ? "Pay in the app (MoMo or card)" : "Pay with MoMo in the app"}
      </Button>
      <Button variant="outline" className="w-full h-12 rounded-xl bg-white text-[16px] font-semibold" disabled={busy} onClick={cash} data-testid="payment-prompt-pay-cash">
        {busy ? <Loader2 className="animate-spin" size={18} /> : <><Banknote size={18} className="mr-2 text-[#34C759]" /> I'll pay cash</>}
      </Button>
      {it.live && <CancelTrip it={it} token={token} onChanged={onChanged} />}
    </Shell>
  );
};

export const ProConfirm = ({ it, token, onHide: hideFn, onChanged }) => {
  const onHide = it.live ? undefined : hideFn;
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      if (it.kind === "laundry_trip") await axios.post(`${API}/rideshare/bookings/${it.id}/laundry-payment-received?token=${token}`, { method: "cash" });
      else if (it.kind === "laundry_bill") await axios.post(`${API}/pro/business/${it.link_id}/laundry/orders/${it.id}/paid?token=${token}`);
      else if (it.rideshare) await axios.post(`${API}/rideshare/bookings/${it.id}/mark-paid?token=${token}`, { method: "cash", amount_received: it.amount });
      else await axios.post(`${API}/bookings/${it.id}/progress?token=${token}`, { step: "paid" });
      celebratePayment({ key: `pp:${it.id}`, amount: it.amount, method: "cash", who: it.customer_name });
      onChanged(); bump();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't confirm the payment"); onChanged(); } finally { setBusy(false); }
  };
  if (it.momo_pending) {
    return (
      <Shell title="MoMo payment" sub="Customer is paying by MoMo" onHide={onHide}>
        <Bill it={it} pro />
        <div className="rounded-2xl bg-white p-4 flex gap-3 items-center" data-testid="payment-prompt-pro-momo-waiting">
          <Loader2 size={20} className="animate-spin text-[#4F988E] shrink-0" />
          <p className="text-[14px] text-gray-700">{first(it.customer_name, "Your customer")} is paying {fmt(it.amount)} by MoMo. Wait for it to go through. Don't take cash as well.</p>
        </div>
      </Shell>
    );
  }
  return (
    <Shell title="Cash payment" sub="Confirm cash received" onHide={onHide}>
      <Bill it={it} pro />
      <p className="text-center text-[15px] text-gray-700" data-testid="payment-prompt-pro-question">Did {first(it.customer_name, "your customer")} pay you {fmt(it.amount)} in cash?</p>
      <Button className="w-full h-12 rounded-xl bg-[#4F988E] hover:bg-[#3d7a72] text-[16px] font-semibold" disabled={busy} onClick={confirm} data-testid="payment-prompt-pro-confirm">
        {busy ? <Loader2 className="animate-spin" size={18} /> : "Yes, I got paid"}
      </Button>
      {it.live && <CancelTrip it={it} token={token} onChanged={onChanged} />}
    </Shell>
  );
};

/** App-wide slide-ups: unpaid bills for customers, cash to confirm for pros. */
export default function PaymentPrompts() {
  const { token, user } = useAuth() || {};
  const { pathname } = useLocation();
  const [data, setData] = useState({ to_pay: [], to_confirm: [] });
  const [momo, setMomo] = useState(null);
  const [paused, setPaused] = useState(false); // another sheet (e.g. "You've arrived") is on screen
  useEffect(() => {
    const onPause = (ev) => setPaused(!!ev?.detail?.paused);
    window.addEventListener("yorm:pay-prompts-pause", onPause);
    return () => window.removeEventListener("yorm:pay-prompts-pause", onPause);
  }, []);
  const [asked, setAsked] = useState(null);
  const [, force] = useState(0);
  const [confirmed, setConfirmed] = useState(null);
  const shownRef = useRef(null);
  const sheetPaidRef = useRef(false);
  const askedSeenRef = useRef(false);
  const role = user?.role;
  const load = useCallback(() => {
    if (!token || !["customer", "artisan"].includes(role)) { setData({ to_pay: [], to_confirm: [] }); return; }
    axios.get(`${API}/payment-prompts?token=${token}`).then((r) => setData(r.data || {})).catch(() => {});
  }, [token, role]);
  const active = !!data.active || [...(data.to_pay || []), ...(data.to_confirm || [])].some((x) => x.momo_pending || x.cash);
  useEffect(() => {
    load();
    const iv = setInterval(load, active ? 5000 : 20000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("yorm:booking-updated", load);
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv); window.removeEventListener("yorm:booking-updated", load);
      window.removeEventListener("focus", load); document.removeEventListener("visibilitychange", onVis);
    };
  }, [load, active]);
  // Live: a job/trip/laundry update over the socket re-checks bills straight away.
  useEffect(() => {
    if (!token || !user?.id) return undefined;
    let ws = null; let retry = null; let closed = false;
    const connect = () => {
      try {
        ws = new WebSocket(`${WS_BASE}/api/ws/${user.id}?token=${token}`);
        ws.onmessage = (e) => {
          try {
            const t = String(JSON.parse(e.data)?.type || "");
            if (/booking|trip|laundry|payment|job|arriv|near/.test(t)) setTimeout(load, 400);
          } catch (_) { /* non-JSON ping */ }
        };
        ws.onclose = () => { if (!closed) retry = setTimeout(connect, 5000); };
      } catch (_) { retry = setTimeout(connect, 5000); }
    };
    connect();
    return () => { closed = true; clearTimeout(retry); try { ws?.close(); } catch (_) { /* already closed */ } };
  }, [token, user?.id, load]);
  // Customer tapped the trip's rate card: bring the pay sheet back (even if hidden).
  useEffect(() => {
    const onOpen = (ev) => {
      const d = ev?.detail;
      if (!d?.id) return;
      try { Object.keys(sessionStorage).filter((k) => k.includes(`:${d.id}:`)).forEach((k) => sessionStorage.removeItem(k)); } catch { /* private mode */ }
      if (d.pro) { force((n) => n + 1); load(); return; }
      setAsked(d);
      load();
    };
    window.addEventListener("yorm:open-payment", onOpen);
    return () => window.removeEventListener("yorm:open-payment", onOpen);
  }, [load]);
  // Pros confirm cash from customers, and pay for rides they booked themselves.
  const list0 = role === "artisan" ? [...(data.to_confirm || []), ...(data.to_pay || []).map((x) => ({ ...x, _pay: true }))] : data.to_pay || [];
  const list = asked && role !== "artisan" && !list0.some((x) => x.id === asked.id)
    ? [asked, ...list0]
    : (asked?.tip ? list0.map((x) => (x.id === asked.id ? { ...x, tip: asked.tip } : x)) : list0); // keep the tip chosen on the "You've arrived" sheet
  // Only inside the signed-in app screens — never on the landing, sign-in or public pages.
  const inApp = !!token && !!user && /^\/(customer|professional|artisan)(\/|$)/.test(pathname) && !/\/payment\/callback/.test(pathname);
  const sc = role === "artisan" && inApp ? data.service_charge : null;
  const mustSettle = !!sc?.must_settle;
  // Once the forced sheet is up it stays until the pro taps Done — even if a payment just took them under the limit.
  const [forcedShown, setForcedShown] = useState(false);
  useEffect(() => { if (mustSettle) setForcedShown(true); else if (!sc) setForcedShown(false); }, [mustSettle, sc]);
  const forcedUp = mustSettle || forcedShown;
  const it = inApp && !forcedUp && !paused ? list.find((x) => !isHidden(x) && !(x.kind === "laundry_trip" && pathname.includes(x.id))) : null;
  const showing = !!it || forcedUp || !!confirmed;
  // The bill on screen just disappeared from the list = it got paid: say so before closing.
  useEffect(() => {
    const prev = shownRef.current;
    shownRef.current = it ? { ...it } : (momo ? shownRef.current : null);
    if (!(prev && !it && !momo && inApp && !list.some((x) => x.id === prev.id))) return;
    if (sheetPaidRef.current) { sheetPaidRef.current = false; return; } // the MoMo sheet already showed its own confirmation
    if (role === "artisan" && !prev._pay) return; // Pros get the global "Payment received" celebration instead
    // Only say "paid" when it really is (not when the trip was cancelled or reported unpaid).
    axios.get(`${API}/hubtel/target-status?target_type=${prev.kind === "laundry_trip" ? "booking" : prev.kind}&target_id=${prev.id}&token=${token}`)
      .then((r) => { if (r.data?.paid) { setConfirmed(prev); playPaymentChime(); } }).catch(() => {});
  }, [it?.id, data]); // eslint-disable-line react-hooks/exhaustive-deps
  // A bill re-opened from the trip page drops off once the server no longer lists it.
  useEffect(() => {
    if (!asked) { askedSeenRef.current = false; return; }
    if (list0.some((x) => x.id === asked.id)) askedSeenRef.current = true;
    else if (askedSeenRef.current) setAsked(null);
  }, [data, asked]); // eslint-disable-line react-hooks/exhaustive-deps
  // Own timer: a reload of `data` while it's showing must not cancel the auto-close.
  useEffect(() => {
    if (!confirmed) return undefined;
    const t = setTimeout(() => setConfirmed(null), 2800);
    return () => clearTimeout(t);
  }, [confirmed]);
  useEffect(() => {
    if (!showing) return;
    blockPopups("payment-prompt");
    return () => unblockPopups("payment-prompt");
  }, [showing]);
  if (forcedUp && sc) {
    return <ServiceChargePaySheet open forced={mustSettle} token={token} owed={sc.owed} threshold={sc.threshold} autoPay={sc.auto_pay}
      onOpenChange={() => { load(); if (!mustSettle) setForcedShown(false); }} onPaid={() => { load(); bump(); }} />;
  }
  if (confirmed && !it && !momo) {
    return (
      <Shell title="Payment confirmed" sub="All settled">
        <div className="rounded-2xl bg-white p-6 flex flex-col items-center text-center gap-2" data-testid="payment-prompt-confirmed">
          <div className="py-3"><ThumbsUpBurst testId="payment-prompt-thumbs-up" /></div>
          <p className="text-[16px] font-semibold text-[#222]">{fmt(confirmed.amount)} paid</p>
          <p className="text-[13px] text-gray-500">Thank you. This closes on its own.</p>
        </div>
      </Shell>
    );
  }
  if (!it && !(momo && inApp)) return null;
  const done = () => { setAsked(null); load(); };
  const hide = () => { setAsked(null); if (!it) return; try { sessionStorage.setItem(hideKey(it), String(Date.now())); } catch { /* private mode */ } force((n) => n + 1); };
  return (
    <>
      {it && !momo && (role === "artisan" && !it._pay
        ? <ProConfirm key={hideKey(it)} it={it} token={token} onHide={hide} onChanged={load} />
        : <CustomerBill key={hideKey(it)} it={it} token={token} onHide={hide} onChanged={done} onMomo={() => (it.kind === "laundry_bill" ? payLaundryBill(it.id, token) : setMomo(it))} />)}
      {momo && (
        <HubtelPaySheet open initialStep="details" onOpenChange={(v) => { if (!v) { setMomo(null); load(); } }} token={token} userPhone={user?.phone}
          target={{ type: momo.kind === "laundry_trip" ? "booking" : momo.kind, id: momo.id, amount: momo.amount, proName: momo.pro_name, label: momo.label, tip: momo.tip || 0, rideshare: !!momo.rideshare || momo.kind === "laundry_trip", payeeNoun: momo.kind === "laundry_bill" ? "the laundry" : undefined }}
          onCashChosen={() => { setMomo(null); done(); bump(); }}
          onPaid={() => { sheetPaidRef.current = true; done(); bump(); }} />
      )}
    </>
  );
}
